import React from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
} from '@mui/material';
import { Description, Refresh } from '@mui/icons-material';
import { PromptVersion } from '../types';

interface PreviewTabProps {
    selectedVersion?: PromptVersion;
    displayPrompt: string;
    onBack: () => void;
    onTest?: () => void;
}

export const PreviewTab: React.FC<PreviewTabProps> = ({
    selectedVersion,
    displayPrompt,
    onBack,
    onTest,
}) => {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, flex: 1 }}>
            {selectedVersion?.status === 'DRAFT' ? (
                <>
                    <Box>
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                            Generated Prompt
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Review your prompt before testing.
                        </Typography>
                    </Box>

                    <Paper
                        elevation={0}
                        sx={{
                            flex: 1,
                            p: 3,
                            bgcolor: '#f8f9fa',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            overflow: 'auto',
                            fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                            fontSize: '0.875rem',
                            lineHeight: 1.6,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            color: 'text.primary',
                            minHeight: '400px',
                            maxHeight: '400px',
                        }}
                    >
                        {displayPrompt || 'No prompt to preview. Please create or select a prompt in the Refine tab.'}
                    </Paper>

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', alignItems: 'center', pb: 3 }}>
                        <Button
                            variant="outlined"
                            onClick={onBack}
                            sx={{ textTransform: 'none', fontWeight: 500 }}
                        >
                            Back to Refine
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<Refresh />}
                            onClick={onTest}
                            sx={{ textTransform: 'none', fontWeight: 600, px: 3 }}
                        >
                            Test with Sample Candidates
                        </Button>
                    </Box>
                </>
            ) : (
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                        p: 4,
                        bgcolor: '#f8f9fa',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                    }}
                >
                    <Description sx={{ fontSize: 64, color: 'text.disabled' }} />
                    <Typography variant="h6" color="text.secondary" textAlign="center">
                        No refined prompt yet.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                        Use the Refine tab to modify the current prompt.
                    </Typography>
                </Box>
            )}
        </Box>
    );
};
